import { Viewer } from '../Viewer';
import { ViewerLayers, ViewerLayersBit } from '../Enums/ViewerLayers';
import { ViewerTool } from './ViewerBaseTool';
import { HsbElement, HsbEntity, SceneLayerName } from '../../hsbcad-model';
import * as THREE from 'three';
import { HsbVisual } from '../../hsb-visual';
import { ViewerScene } from '../ViewerScene';
import { PointerButtons } from '../Events/ViewerEvents';
import { SVGOverlay } from '../Overlays/SVGOverlay';
import { Overlay } from '../Overlays/Overlay';
import { Picker } from './Picker';
import { PostEffects } from '../PostProcessing';
import { Wireframe } from 'three/examples/jsm/lines/Wireframe';
import { Sides } from '../ViewCube';
import Issue from '../Data/Issue';

type ShowHideProps<T> = T extends THREE.InstancedMesh
    ? { object: THREE.InstancedMesh; instanceid: number }
    : T extends THREE.Object3D
    ? { object: THREE.Object3D }
    : never;

/**
 * THe selection tool is used to select, hover and isolate entities.
 */
export class SelectionTool extends ViewerTool {
    _picker: Picker;

    private _currentSelection: (HsbEntity | HsbElement)[] = [];
    public set currentSelection(entities: (HsbEntity | HsbElement)[]) {
        this._currentSelection = entities;
    }
    public get currentSelection() {
        return this._currentSelection;
    }
    _currentHover: (HsbEntity | HsbElement)[] = [];
    private _currentIssue: Issue | null = null;
    /**
     *description
     */
    public get CurrentIssue(): Issue | null {
        return this._currentIssue;
    }
    /**
     *description
     */
    public set CurrentIssue(v: Issue | null) {
        if (this._currentIssue !== v) this._toolEvent?.emit('issue', { issue: v, action: 'hover' });
        this._currentIssue = v;
    }

    private _selectElement: boolean = false;
    public get SelectElement(): boolean {
        return this._selectElement;
    }
    public set SelectElement(v: boolean) {
        this._selectElement = v;
    }

    get _SVGOverlay(): SVGOverlay {
        return this._viewer.SVGOverlay;
    }
    get _Overlay(): Overlay {
        return this._viewer.Overlay;
    }

    public set MultiSelect(state: boolean) {
        this._multiSelect = state;
    }

    public get MultiSelect(): boolean {
        return this._multiSelect;
    }
    private _multiSelect: boolean = false;

    constructor(viewer: Viewer) {
        super(viewer);
        this.Tool = 'Selection';
        this._picker = this._viewer._picker;
    }

    SetScene(scene: ViewerScene) {
        //this._viewer.postProcessingManager.EnableEffect(PostEffects.OUTLINE);
        this.currentSelection = [];
        this._currentHover = [];
    }
    OnPrimaryUp(coords: THREE.Vector2, event: any): void {
        let request = this._picker.requestRaycast(coords, {
            layer:
                ViewerLayersBit.MODELS |
                ViewerLayersBit.LABELS |
                ViewerLayersBit.SIMPLEMODEL |
                ViewerLayersBit.ISSUES,
            onlyReceiveFirst: false,
        });
        // If there are intersects
        if (request?.intersection && request.intersection.length > 0) {
            let intersect = request.intersection[0];
            if (
                (intersect.object.layers.mask & ViewerLayersBit.ISSUES) ===
                ViewerLayersBit.ISSUES
            ) {
                let issue = intersect.object.userData.issue;
                if (issue) {
                    this.Deselect(this._currentSelection);
                    this._toolEvent?.emit('issue', { issue, action: 'click' });
                    this._viewer.requestRenderFrame();
                    return;
                }
            }

            // Get first relevant visual
            if (!intersect.object.userData.handle) return;

            let instanceId = intersect.instanceId;
            let handle =
                instanceId !== undefined
                    ? intersect.object.userData.handle[instanceId]
                    : intersect.object.userData.handle;

            let entity =
                this._viewer.project?.EntityTable[intersect.object.userData.modelId][handle];
            if (!entity) return;
            if (this.SelectElement && entity instanceof HsbEntity)
                entity = entity.getElement() ?? entity;
            (this.currentSelection.includes(entity!)
                ? this.Deselect(entity!)
                : this.Select(entity!)
            ).then((res) => {
                this._toolEvent?.emit('selection', { event: event, ...res });
                this._viewer.requestRenderFrame();
            });
        }
    }

    onSecondaryDown(coords: THREE.Vector2, e: any) {
        this.CancelSelection().then((res) => {
            this._toolEvent?.emit('selection', { event: e, ...res });
            this._viewer.requestRenderFrame();
        });
    }

    Deactivate() {
        this.cleanUp();
    }

    cleanUp() {
        Promise.all([this.Deselect(this.currentSelection), this.cancelHover()]).then((res) => {
            this._viewer.requestRenderFrame();
        });
    }

    ZoomToSelection() {
        this._viewer.cameraUtils.FocusCameraOnEntities(this.currentSelection, {
            offset: 10,
            tween: true,
        });
    }

    HideSelection() {
        let self = this;
        // Hide selection
        let currentSelection = Array.from(this.currentSelection);
        this.Deselect(this.currentSelection);

        currentSelection.forEach((entity) => {
            if (entity.object instanceof THREE.InstancedMesh) {
                // Hide logic for instanced
                let instanceId = entity.instanceId;
                this._viewer.HideObject(entity.object, instanceId);
                entity.object?.children.forEach((obj) => {
                    obj.traverse((obj) => {
                        self._viewer.HideObject(obj);
                    });
                });
            } else {
                entity.object?.traverse((obj) => {
                    self._viewer.HideObject(obj);
                });
            }
        });
        this._toolEvent?.emit('hideSelection', this.currentSelection);
        this._viewer.ViewerEvents?.VisibilityEvents.emit(
            'visibility',
            this.currentSelection,
            false,
            { source: this }
        );
        this.currentSelection = [];
        this._viewer.requestRenderFrame();
    }
    IsolateSelection() {
        // Getting rid of current hover
        if (this._currentHover) {
            this.cancelHover();
            this._currentHover = [];
        }
        let entities = Array.from(this.currentSelection);
        this.IsolateEntities(entities).then(() => {
            this._viewer.requestRenderFrame();
        });
    }
    async IsolateEntities(entities: HsbEntity[]) {
        if (!this._viewer.currentScene || entities.length === 0) return;

        // If a group (not subgroup) use topview, else diagonal frontlefttop
        let dir =
            entities[0].objecttype === 'notset' && !entities[0].node.subgroup
                ? Sides.Top
                : Sides.Top | Sides.Front | Sides.Left;
        // Hide everything
        this._viewer.currentScene.GetLayer(SceneLayerName.Entities).children.forEach((c) => {
            c.traverseVisible((obj) => {
                this._viewer.HideObject(obj);
            });
        });
        // Show selection
        this.ShowEntities(entities);
        await this._viewer.cameraUtils.FocusCameraOnEntities(entities, {
            offset: 10,
            tween: true,
            sides: dir,
        });

        this._toolEvent?.emit('hideSelection', null);
        this._viewer.ViewerEvents?.VisibilityEvents.emit(
            'visibility',
            this.currentSelection,
            true,
            { source: this }
        );
        this.Deselect(this.currentSelection);

        // De select
        this.currentSelection = [];
        this._toolEvent?.emit('selection', this.currentSelection);
    }

    ShowEntities(entities: HsbEntity[]) {
        entities.forEach((entity) => {
            if (entity.object instanceof THREE.InstancedMesh) {
                let instanceId = entity.instanceId;
                this._viewer.ShowObject(entity.object, instanceId);
                entity.object?.traverseAncestors((obj) => {
                    this._viewer.ShowObject(obj);
                });
            } else {
                entity.object?.traverse((obj) => {
                    this._viewer.ShowObject(obj);
                });
                entity.object?.traverseAncestors((obj) => {
                    this._viewer.ShowObject(obj);
                });
            }
        });
    }

    ShowAll() {
        let self = this;
        return new Promise((resolve, reject) => {
            if (!self._viewer.currentScene) return reject('No scene active currently');
            self._viewer.currentScene.GetLayer(SceneLayerName.Entities).traverse((obj) => {
                this._viewer.ShowObject(obj);
            });
            this._viewer.ViewerEvents?.VisibilityEvents.emit('visibility', null, true, {
                source: this,
            });
            this._toolEvent?.emit('showSelection', null);
            this.CancelSelection();
            resolve({ type: 'visible', entities: null });
        });
    }
    OnPointerMove(coords: THREE.Vector2, type: string, event: any): void {
        let self = this;
        // if (this._pointerState.hasFlag(PointerButtons.PRIMARY)) return;
        let request = this._picker.requestRaycast(coords, {
            layer: ViewerLayersBit.MODELS | ViewerLayersBit.LABELS | ViewerLayersBit.ISSUES,
            onlyReceiveFirst: false,
        });
        // iF there are intersects
        if (request?.intersection && request?.intersection?.length > 0) {
            // Get first relevant visual
            let intersect = request.intersection[0];

            if (
                (intersect.object.layers.mask & ViewerLayersBit.ISSUES) ===
                ViewerLayersBit.ISSUES
            ) {
                if (this._currentHover.length > 0) this.cancelHover();
                let issue = intersect.object.userData.issue;
                if (issue !== this._currentIssue)
                    this.CurrentIssue = intersect.object.userData.issue;
                this._viewer.requestRenderFrame();
                return;
            }
            this.CurrentIssue = null;

            let instanceId = intersect.instanceId;
            let handle =
                instanceId !== undefined
                    ? intersect.object.userData.handle[instanceId]
                    : intersect.object.userData.handle;
            let entity =
                this._viewer.project?.EntityTable[intersect.object.userData.modelId]?.[handle];
            if (!entity) return;
            // Selection has precedence over hover
            if (this.currentSelection.includes(entity)) return;
            if (this.SelectElement && entity instanceof HsbEntity)
                entity = entity.getElement() ?? entity;
            if (this._currentHover.length > 0) {
                if (!this._currentHover.includes(entity!)) {
                    //Unhover what was hovered
                    this.cancelHover();
                } else {
                    return;
                }
            }
            // hover
            self.Hover(entity!)
                .then((res: any) => {
                    this._toolEvent?.emit('hover', { event: event, ...res });
                })
                .catch((err) => {});
            this._viewer.requestRenderFrame();
        }
        // Else get rid oif the current hover
        else if (this._currentHover.length > 0) {
            // Unhover
            this.cancelHover();
            this._viewer.requestRenderFrame();
            this._currentHover = [];
        }
    }

    OnDoubleClick(coords: THREE.Vector2, button: string, buttons: PointerButtons, event: any) {
        let intersect = this._picker.requestRaycast(coords, {
            layer: ViewerLayersBit.MODELS | ViewerLayersBit.LABELS,
            onlyReceiveFirst: true,
        });
        if (intersect) {
            let instanceId = intersect.intersection.instanceId;
            let handle =
                instanceId !== undefined
                    ? intersect.object.userData.handle[instanceId]
                    : intersect.object.userData.handle;
            let entity =
                this._viewer.project?.EntityTable[intersect.object.userData.modelId]?.[handle];
            if (!entity) return;
            this.Select(entity).then((res) => {
                this._toolEvent?.emit('selection', { event: event, ...res });
            });
            this._viewer.cameraUtils.FocusCameraOnEntities(this.currentSelection, {
                offset: 25,
                tween: true,
            });
        }
    }

    Deselect(
        entities: (HsbEntity | HsbElement)[] | (HsbEntity | HsbElement)
    ): Promise<{ type: string; entities: (HsbEntity | HsbElement)[] }> {
        return new Promise((resolve, reject) => {
            if (!entities) reject('No entities provided');

            if (Array.isArray(entities)) {
                // Resetting to proper material
                entities.forEach((entity) => {
                    if (entity.object instanceof THREE.InstancedMesh) {
                        // instanced
                        let instanceId = entity.object.userData.handle?.findIndex(
                            (h: string) => h === entity.handle
                        );
                        entity.object.setColorAt(
                            instanceId,
                            new THREE.Color(entity.object.userData.color[instanceId])
                        );
                        if (entity.object.instanceColor)
                            entity.object.instanceColor.needsUpdate = true;
                    } else if (entity.object instanceof THREE.Mesh)
                        // regular mesh
                        entity.object.material = entity.object.userData.originalmaterial;
                });
                // Filter out the deselected visuals
                this.currentSelection = this.currentSelection.filter(
                    (el) => !entities.includes(el)
                );

                resolve({ type: 'selection', entities: this.currentSelection });
            } else {
                if (entities.object instanceof THREE.InstancedMesh) {
                    // instanced
                    let instanceId = entities.object.userData.handle?.findIndex(
                        (h: string) => h === entities.handle
                    );
                    entities.object.setColorAt(
                        instanceId,
                        new THREE.Color(entities.object.userData.color[instanceId])
                    );
                    if (entities.object.instanceColor)
                        entities.object.instanceColor.needsUpdate = true;
                } else if (entities.object instanceof THREE.Mesh)
                    // regular mesh
                    entities.object.material = entities.object.userData.originalmaterial;
                // Filter out the deselected visuals
                this.currentSelection = this.currentSelection.filter((el) => el !== entities);
                resolve({ type: 'selection', entities: this.currentSelection });
            }
        });
    }

    CancelSelection() {
        return this.Deselect(this.currentSelection);
    }

    private getEntityArray(entity: (HsbElement | HsbEntity) | (HsbElement | HsbEntity)[]) {
        let targetEntities: HsbEntity[] = [];
        // Collecting all entities in array
        if (Array.isArray(entity)) {
            targetEntities = entity.reduce((accu: HsbEntity[], e) => {
                if (e instanceof HsbElement) {
                    accu.push(e, ...e.getEntities());
                } else {
                    accu.push(e);
                }
                return accu;
            }, []);
            // Filter out duplicates
            targetEntities = targetEntities.filter((e, index) => {
                targetEntities.findIndex(
                    (ent) => ent.handle === e.handle && ent.hsbmodel.modelId === e.hsbmodel.modelId
                ) === index;
            });
        } else if (entity instanceof HsbElement) {
            targetEntities = [entity, ...entity.getEntities()];
        } else {
            targetEntities = [entity];
        }
        return targetEntities;
    }

    Select(
        entity: (HsbElement | HsbEntity) | (HsbElement | HsbEntity)[],
        forceMultiSelect: boolean = false
    ): Promise<{ type: string; entities: (HsbEntity | HsbElement)[] }> {
        return new Promise((resolve, reject) => {
            // Deselect if we are nto in multiselect
            if (!(this._multiSelect || forceMultiSelect)) {
                this.Deselect(this.currentSelection);
                this.currentSelection = [];
            } else if (
                Array.isArray(entity)
                    ? entity.every((e) => this.currentSelection.includes(e))
                    : this.currentSelection.includes(entity)
            )
                return resolve({
                    type: 'selection',
                    entities: this.currentSelection,
                });

            var targetEntities: HsbEntity[] = this.getEntityArray(entity);
            this.currentSelection.push(...targetEntities);

            // Unhover if part of new selection
            let selectedHovers = this._currentHover.filter((h) =>
                this.currentSelection.includes(h)
            );
            if (selectedHovers.length > 0) {
                this.UnHover(selectedHovers);
                this._currentHover = [];
            }

            this.applySelectionMaterial(targetEntities);
            resolve({ type: 'selection', entities: this.currentSelection });
        });
    }

    private applySelectionMaterial(entities: HsbEntity[]) {
        // Selection logic
        entities.forEach((ent) => {
            if (ent.object instanceof THREE.InstancedMesh) {
                // instanced
                let instanceId = ent.object.userData.handle?.findIndex(
                    (h: string) => h === ent.handle
                );
                ent.object.setColorAt(instanceId, new THREE.Color(0x7094ff));
                ent.object.instanceColor!.needsUpdate = true;
            } else if (ent.object instanceof THREE.Mesh)
                // regular mesh
                ent.object.material = this._viewer._materialManager?.getSelectMaterial();
        });
    }

    Hover(entity: HsbEntity | HsbElement) {
        return new Promise((resolve, reject) => {
            if (!entity) return reject('No entities provided!');

            var targetEntities: HsbEntity[] = this.getEntityArray(entity);
            this._currentHover.push(...targetEntities);

            if (this.SelectElement && entity instanceof HsbEntity) {
                entity = entity.getElement();
            }

            this.applyHoverMaterial(targetEntities);
            resolve({ type: 'hover', entities: this._currentHover });
        });
    }

    private applyHoverMaterial(entities: HsbEntity[]) {
        entities.forEach((ent) => {
            if (ent.object instanceof THREE.InstancedMesh) {
                // instanced
                let instanceId = ent.object.userData.handle?.findIndex(
                    (h: string) => h === ent.handle
                );
                if (instanceId !== undefined && instanceId > -1) {
                    ent.object.setColorAt(instanceId, new THREE.Color(0xf5fc03));
                    ent.object.instanceColor!.needsUpdate = true;
                }
            } else if (ent.object instanceof THREE.Mesh)
                ent.object.material = this._viewer._materialManager?.getHighlightMaterial();
        });
    }

    UnHover(entities: (HsbEntity | HsbElement)[]) {
        return new Promise((resolve, reject) => {
            // Unhover current
            if (!entities) return reject('No entities provided!');
            // DOnt override selection
            let [toUnhover, selected] = entities.reduce(
                (
                    [pass, fail]: [(HsbEntity | HsbElement)[], (HsbEntity | HsbElement)[]],
                    entity
                ) => {
                    if (!this.currentSelection.includes(entity)) return [[...pass, entity], fail];
                    else return [pass, [...fail, entity]];
                },
                [[], []]
            );
            this.applySelectionMaterial(selected);
            // Perform unhover for all entities
            toUnhover.forEach((entity) => {
                if (entity.object instanceof THREE.InstancedMesh) {
                    // instanced
                    let instanceId = entity.object.userData.handle?.findIndex(
                        (h: string) => h === entity.handle
                    );
                    if (instanceId !== undefined && instanceId > -1) {
                        entity.object.setColorAt(
                            instanceId,
                            new THREE.Color(entity.object.userData.color[instanceId])
                        );
                        entity.object.instanceColor!.needsUpdate = true;
                    }
                } else if (entity.object instanceof THREE.Mesh)
                    entity.object.material = entity.object.userData.originalmaterial;
            });
            this._currentHover = this._currentHover.filter((h) => !entities.includes(h));
            resolve({ type: 'hover', entities: this._currentHover });
        });
    }

    cancelHover() {
        this.CurrentIssue = null;
        return this.UnHover(this._currentHover);
    }

    //#endregion

    OnKeyPressed(keycode: string): void {
        switch (keycode) {
            case 'ControlLeft':
            case 'ControlRight':
                this.MultiSelect = true;
                break;
            case 'ShiftLeft':
            case 'ShiftRight':
                this._selectElement = true;
                break;
        }
    }
    OnKeyReleased(keycode: string): void {
        switch (keycode) {
            case 'ControlLeft':
            case 'ControlRight':
                this.MultiSelect = false;
                break;
            case 'ShiftLeft':
            case 'ShiftRight':
                this._selectElement = false;
                break;
        }
    }
}
